import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from "@vaiu/ui/card";
import { Shield, AlertTriangle } from 'lucide-react';
import { Button } from "@vaiu/ui/button";
import { hasAdminAccess, getAdminRoleType } from '@/utils/rolePermissions';

interface ProtectedAdminRouteProps {
  children: React.ReactNode;
}

const ProtectedAdminRoute: React.FC<ProtectedAdminRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();

  // Show loading state while auth is being determined
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-96">
          <CardContent className="flex items-center justify-center p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-gray-600">Loading...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has admin access (either ADMIN or CUSTOMER_SUPPORT)
  const hasAccess = hasAdminAccess(user.roles || []);
  const adminRoleType = getAdminRoleType(user.roles || []);

  // Debug info for development
  if (import.meta.env.DEV) {
    console.log('🔐 Admin Access Check:', {
      user: user.username || user.emails?.[0]?.address,
      roles: user.roles,
      hasAdminAccess: hasAccess,
      adminRoleType: adminRoleType
    });
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-96">
          <CardContent className="p-8">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Access Denied
              </h2>
              
              <p className="text-gray-600 mb-6">
                You don't have permission to access the admin panel. 
                Administrator or Customer Support privileges are required.
              </p>
              
              <div className="space-y-3">
                <Button 
                  onClick={() => window.history.back()}
                  variant="outline"
                  className="w-full"
                >
                  Go Back
                </Button>
                
                <Button 
                  onClick={() => window.location.href = '/'}
                  className="w-full"
                >
                  Return to Home
                </Button>
              </div>
              
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  If you believe this is an error, please contact your system administrator.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // User is authenticated and has admin access, render the admin content
  return <>{children}</>;
};

export default ProtectedAdminRoute; 